import { useCallback, useEffect, useRef, useState } from 'react';
import { usePartnerDataClient } from '../api/use-partner-data-client';
import { ControlAction } from '../domain/enums';
import { ControlResult, IngestionDetail } from '../domain/types';
import { isTerminalStatus } from '../utils/ingestion-status.utils';
import { isTabVisible } from '../utils/tab-visibility.util';
import { useAsyncResource } from './use-async-resource';

/**
 * Detail polls faster than the list: an operator on this page is usually
 * watching one specific file move through the pipeline.
 */
const DEFAULT_POLL_INTERVAL_MS = 2000;

interface ShouldPollParams {
  skip: boolean;
  ingestion: IngestionDetail | undefined;
  hasError: boolean;
}

/**
 * Whether the detail should keep polling.
 *
 * Three cases, in order:
 * - `skip`: no event id, so there is nothing to poll for.
 * - Nothing loaded: only worth retrying if the last attempt failed. Without
 *   this a failed *first* fetch would leave the page on the error panel with
 *   nothing to retry it, since the poll condition is otherwise derived from
 *   an ingestion that does not exist yet.
 * - Loaded: keep going until the ingestion reaches a terminal status.
 */
const shouldKeepPolling = ({
  skip,
  ingestion,
  hasError,
}: ShouldPollParams): boolean => {
  if (skip) {
    return false;
  }

  if (!ingestion) {
    return hasError;
  }

  return !isTerminalStatus(ingestion.status);
};

export interface UseIngestionDetailParams {
  /** Set to 0 to disable polling (used by tests). */
  pollIntervalMs?: number;
  /**
   * Skip fetching entirely — used when the route has not supplied an event
   * id yet, so the module never asks the BFF for an empty id.
   */
  skip?: boolean;
}

export interface UseIngestionDetailReturn {
  ingestion: IngestionDetail | undefined;
  isLoading: boolean;
  hasError: boolean;
  isControlling: boolean;
  refetch: () => void;
  controlIngestion: (action: ControlAction) => Promise<ControlResult>;
}

/**
 * Fetches a single ingestion by event id and exposes a `controlIngestion`
 * action (pause/resume/cancel) that automatically refetches afterwards.
 *
 * While the ingestion is in a non-terminal status the hook polls the port so
 * the timeline and progress advance without the operator refreshing; polling
 * stops as soon as it reaches a terminal status.
 */
export const useIngestionDetail = (
  eventId: string,
  {
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    skip = false,
  }: UseIngestionDetailParams = {},
): UseIngestionDetailReturn => {
  const client = usePartnerDataClient();
  const [isControlling, setIsControlling] = useState(false);

  const fetchIngestion = useCallback(
    () => (skip ? Promise.resolve(undefined) : client.getIngestion(eventId)),
    [client, eventId, skip],
  );

  const {
    data: ingestion,
    isLoading,
    hasError,
    refetch,
  } = useAsyncResource<IngestionDetail | undefined>(
    fetchIngestion,
    undefined,
    // A transient poll failure must not blank the page or, worse, stop the
    // poll for good — `shouldPoll` below is derived from the ingestion, so
    // clearing it on error would permanently kill live updates.
    { keepPreviousDataOnError: true },
  );

  // Poll via a ref so the interval always calls the latest `refetch` without
  // being torn down and recreated on every render.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const shouldPoll = shouldKeepPolling({ skip, ingestion, hasError });

  useEffect(() => {
    if (!shouldPoll || pollIntervalMs <= 0) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      if (isTabVisible()) {
        refetchRef.current();
      }
    }, pollIntervalMs);

    return (): void => clearInterval(intervalId);
  }, [shouldPoll, pollIntervalMs]);

  const controlIngestion = async (
    action: ControlAction,
  ): Promise<ControlResult> => {
    setIsControlling(true);

    try {
      const result = await client.controlIngestion(eventId, action);
      refetch();

      return result;
    } finally {
      setIsControlling(false);
    }
  };

  return {
    ingestion,
    isLoading,
    hasError,
    isControlling,
    refetch,
    controlIngestion,
  };
};
